// Modules that force their own theme regardless of the page's declared
// `c_colorTheme`, by self-wrapping in `<Chakra3Theme>` (see
// `app/leap/modules/text-banner/index.jsx`). Kept in sync with any module that
// does this — currently only `text-banner`, which always renders light.
const MODULE_FORCED_THEME = {
    'leap-text-banner': 'light'
}

// The `background.section.*` token path for each `background` variant
// `ModuleContainer` accepts. `inverse` has no color of its own (see
// `ModuleContainer`) and paints as `strong`, so it reads that token too.
// Exported as plain token paths — not resolved vars — so `ModuleContainer`'s
// own recipe (`module-container/index.recipe.js`) can import this instead of
// re-declaring the same variant→token mapping: Panda resolves a token path
// written directly in a recipe at build time, same as any other recipe
// color, and can't do the same for a value that's already been resolved.
export const SECTION_BG_VAR = {
    default: 'background.section.default',
    subtle: 'background.section.subtle',
    strong: 'background.section.strong',
    inverse: 'background.section.strong'
}

/**
 * Derives the Xleap header/breadcrumb background from the first module's own
 * `background` variant (`default`/`subtle`/`strong`/`inverse`, the same prop
 * `ModuleContainer` takes) resolved against the theme that module actually
 * renders in — i.e. the exact panel color the first module itself paints, not
 * a generic theme-only guess.
 *
 * Most modules inherit the page's own `Chakra3Theme` (`c_colorTheme`), so the
 * first module's theme is the page theme in the common case; a module can
 * still render in a different theme by self-wrapping (`MODULE_FORCED_THEME`).
 *
 * Returns the CSS var for the variant rather than a resolved literal: the
 * caller is expected to publish `theme` as `data-leap-theme` on `<html>`
 * (e.g. via `Seo`/`Helmet`), which is the scope
 * `--leap-colors-background-section-*` itself resolves light/dark against
 * (`app/leap/theme/index.js`), so the light/dark values only ever live once,
 * in the token definitions.
 *
 * @param {import('@chakra-ui/react3').SystemContext} system - The active Chakra v3
 *   system (`useChakraContext()`), used to resolve the token path to its CSS var.
 * @param {'light'|'dark'} [pageTheme] - The page's own `c_colorTheme`.
 * @param {Array<{moduleType: string, background?: string}>} [modules] - The page's CMS modules, in order.
 * @returns {{theme: 'light'|'dark', bg: string}|undefined} The theme the first
 *   module renders in and the CSS var for its background, or `undefined` when
 *   there is no first module to read yet (loading/empty) — callers should
 *   fall back to `'background.section.subtle'` in that case, per the ticket's
 *   stated fallback.
 */
export const getHeaderBgFromFirstModule = (system, pageTheme, modules) => {
    const firstModule = Array.isArray(modules) ? modules[0] : undefined
    if (!firstModule) {
        return undefined
    }
    const theme = MODULE_FORCED_THEME[firstModule.moduleType] || pageTheme || 'light'
    const variant = SECTION_BG_VAR[firstModule.background] ? firstModule.background : 'default'
    return {theme, bg: system.css({color: SECTION_BG_VAR[variant]}).color}
}
